/**
 * Puente a los callables del núcleo desde el widget (solo en modo live). La
 * reserva y el cobro corren server-side (Estrategia A en `reservationBook`): el
 * navegador solo manda el token de la tarjeta + los datos del pasajero, nunca
 * el número de tarjeta. En mock, el flujo lo lleva el motor local (engineFlow).
 */
import { httpsCallable } from 'firebase/functions'
import { getFirebase } from '@/firebase'

export interface BookInput {
  reservationId: string
  /** Compatibilidad con el flujo de una sola tarifa; hoy manda el carrito. */
  roomRef?: string
  holder: { firstName: string; lastName: string }
  occupants: Array<{ name: string; lastName: string; type: 'adult' | 'child'; age: number }>
  cardToken: string
  paymentMethodId: string
  /** Banco emisor (lo devuelve el Brick; MercadoPago lo exige en México). */
  issuerId?: string
  payerEmail: string
  /** WhatsApp del titular. CVU lo usa como identificador único de la venta. */
  payerPhone: string
  installments?: number
  identification?: { type: string; number: string }
}

export interface BookResult {
  status: string
  locator?: string | null
  price?: number
  currency?: string
}

/**
 * Agregar o quitar del viaje → el backend republica el carrito en el chat.
 *
 * Ya no publica el paso de pago: eso pasó a `irAPagar`, porque elegir un tour
 * dejó de significar "termina aquí" y pasó a significar "ponlo en el viaje".
 */
export async function selectRoom(
  reservationId: string,
  roomRef: string,
  accion: 'agregar' | 'quitar' = 'agregar',
): Promise<void> {
  const fb = getFirebase()
  if (!fb) throw new Error('Firebase no disponible.')
  const fn = httpsCallable<
    { reservationId: string; roomRef: string; accion: 'agregar' | 'quitar' },
    unknown
  >(fb.functions, 'reservationSelect')
  await fn({ reservationId, roomRef, accion })
}

/** El viaje completo, un solo cargo: publica el paso de pago con el total. */
export async function irAPagar(reservationId: string): Promise<void> {
  const fb = getFirebase()
  if (!fb) throw new Error('Firebase no disponible.')
  const fn = httpsCallable<{ reservationId: string }, unknown>(fb.functions, 'reservationCheckout')
  await fn({ reservationId })
}

/** Reserva + cobra (server-side). El recibo llega al chat por realtime. */
export async function bookReservation(input: BookInput): Promise<BookResult> {
  const fb = getFirebase()
  if (!fb) throw new Error('Firebase no disponible.')
  const fn = httpsCallable<BookInput, BookResult>(fb.functions, 'reservationBook')
  const res = await fn(input)
  return res.data
}
